import React, {useEffect, useState} from "react";
import useApiCallService from "../../Common/Helpers/ApiCall";
import ResponseReimbursement from "../Models/Response/ResponseReimbursement";
import SortableList from "../../Common/Components/Lists/SortableList";
import {CustomButton} from "../../Common/Components/Button/CustomButton";
import {Button, Col, Row} from "reactstrap";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {ADMIN_PATHS, REPORT_PROCEEDING_PATHS, USER_PATHS} from "../../config";
import {useTranslation} from "react-i18next";
import {ToCustomDecimal} from "../../AppSystem/Helpers/ToCustomDecimal";
import {CheckUserPermission} from "../../AppSystem/Helpers/CheckUserPermission";
import {UrlStructure} from "../../AppSystem/Helpers/UrlStructure";
import ResponseUserSettingsModel from "../../UserSettings/Model/Response/ResponseUserSettingsModel";
import {CustomModal} from "../../Common/Components/Modal/CustomModal";
import {CustomReload} from "../../AppSystem/Helpers/CustomReload";
import {InfoPageMessage} from "../../Common/Components/Button/InfoPageMessage";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import ReimbursementHeader from "./ReimbursementHeader";
import ReimbursementHeaderMobile from "./ReimbursementHeaderMobile";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import ReportProceedingInfo from "./ReportProceedingInfo";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";
import { SelectOrgPanel } from "../Components/SelectOrgPanel";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { ReimbursementTypeBadges, getReimbursementTypesFromReimbursement } from "../../Common/Components/Badges/ReimbursementTypeBadges";

// Month selector + summary boxes (Inväntar godkännande / Nekade / Förslag). Hidden at customer request; set to true to restore.
const SHOW_SUMMARY_BOXES = false;

const ReportProceedingList = ({ userId = null, returnPath = null }) => {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();
    const [userHasBankAccount, setUserHasBankAccount] = useState(null);
    const isMobile = CheckMobile(998);
    const location = useLocation();
    const [mobileShowAll, setMobileShowAll] = useState(() => {
        const searchParams = new URLSearchParams(window.location.search);
        return isMobile && searchParams.get("mobileShowAll") === "1";
    });

    const buildReturnToWithShowAll = () => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("mobileShowAll", "1");
        const queryString = searchParams.toString();
        return `${location.pathname}${queryString ? `?${queryString}` : ""}`;
    };

    useEffect(() => {
        setPageTitle(t('reimbursement.reportProcessings'));
        setPageTitleKey('reimbursement.reportProcessings');
        if (isMobile && mobileShowAll) {
            setOnBack(() => setMobileShowAll(false));
        } else {
            setOnBack(null);
        }
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t, isMobile, mobileShowAll]);

    const baseUrl = "/reimbursement/search/filter";
    const query = userId && userId !== "" && userId !== "undefined"
        ? `?userId=${userId}`
        : "";

    const isReadOnly = userId && userId !== "" && userId !== "undefined";

    const navigate = useNavigate();

    const [deleteApiResponse, setDeleteApiResponse] = useState(null);
    const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
    const [reimbursementId, setReimbursementId] = useState(null);

    const [panelOpen, setPanelOpen] = useState(false);
    const [panelItemId, setPanelItemId] = useState(null);
    const [infoPanelVisualId, setInfoPanelVisualId] = useState(null);

    const openPanel = (id, visualId = null) => {
        setPanelItemId(id);
        setInfoPanelVisualId(visualId);
        setPanelOpen(true);
    };

    const [selectOrgOpen, setSelectOrgOpen] = useState(false);

    let permission = false;
    if (CheckUserPermission("user.reimbursement.create") || CheckUserPermission("user.reimbursement") || CheckUserPermission("user")) {
        permission = true;
    }

    let permissionDelete = false;
    if (CheckUserPermission("user.reimbursement.delete") || CheckUserPermission("user.reimbursement") || CheckUserPermission("user")) {
        permissionDelete = true;
    }

    // HANDLE MODAL DELETE
    const handleModalDelete = async () => {
        const apiCallOptions = {
            method: "delete",
            url: `/reimbursement/`,
            data: { id: reimbursementId }
        };
        try {
            await ApiCall(apiCallOptions, setDeleteApiResponse);
            setDisplayDeleteModal(false);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (deleteApiResponse !== null) {
            CustomReload(REPORT_PROCEEDING_PATHS.REPORT_PROCEEDING_LIST);
        }
    }, [deleteApiResponse]);

    const modalContent = (
        <div>{`${t('validationMessage.modalVar')}?`}</div>
    );
    const modalFooter = (
        <>
            <Button className="box-green border-0" onClick={handleModalDelete}>
                {t('button.delete')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayDeleteModal(false)}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const renderActionColumn = (item) => {
        return (
            <>
                {item.status === "Förslag" ? (
                    <>
                        {!isReadOnly && (
                            <Button
                                className="bg-green-light text-green border-0 position-relative reimbursement-report-action-btn"
                                onClick={() => navigate(
                                    `${REPORT_PROCEEDING_PATHS.REPORT_PROCEEDING}?id=${item.id}`,
                                    {
                                        state: {
                                            returnTo: (isMobile && mobileShowAll)
                                                ? buildReturnToWithShowAll()
                                                : `${location.pathname}${location.search}`,
                                        },
                                    }
                                )}
                            >
                                <i className="bx bxs-pencil fs-5 align-middle pb-1" />
                                <div className="tooltip-custom-button">{t("button.edit")}</div>
                            </Button>
                        )}
                        {!isReadOnly && permissionDelete === true &&
                            <Button className="box-red border-0 position-relative me-2 rounded reimbursement-report-action-btn" onClick={() => {
                                setDisplayDeleteModal(true);
                                setReimbursementId(item.id);
                            }}>
                                <i className="bx bx-minus fs-5 align-middle pb-1"/>
                                <div className="tooltip-custom-button">{t("button.delete")}</div>
                            </Button>
                        }
                    </>
                ) : (
                    <Button
                        className="bg-green-light text-green border-0 position-relative reimbursement-report-action-btn"
                        onClick={() => openPanel(item.id, item.visualId)}
                    >
                        <i className="bx bx-show fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">{t("button.info")}</div>
                    </Button>
                )}
            </>
        );
    };

    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    });
    const [summaryItems, setSummaryItems] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(false);

    useEffect(() => {
        const [year, monthNum] = selectedMonth.split("-").map(Number);
        const start = `${year}-${String(monthNum).padStart(2, "0")}-01`;
        const lastDay = new Date(year, monthNum, 0).getDate();
        const end = `${year}-${String(monthNum).padStart(2, "0")}-${lastDay}`;
        const url = `/reimbursement/search/filter?datefromdate=${start}&datetodate=${end}${userId ? `&userId=${userId}` : ""}&page=1&perPage=100000`;

        setSummaryLoading(true);
        ApiCall({ method: "get", url }, (response) => {
            setSummaryLoading(false);
            setSummaryItems(response?.items ?? []);
        });
    }, [selectedMonth, userId]);

    useEffect(() => {
        const checkBankAccount = async () => {
            const apiCallOptions = {
                method: "get",
                url: `/reimbursement/v2/info`
            };
            await ApiCall(apiCallOptions, (response) => {
                const account = response?.userBankAccount;
                const hasBankAccount = account?.clearingNumber !== undefined && account?.accountNumber !== undefined;
                setUserHasBankAccount(hasBankAccount);
            });
        };
        checkBankAccount();
    }, []);

    const changeMonth = (delta) => {
        const [year, month] = selectedMonth.split("-").map(Number);
        const d = new Date(year, month - 1 + delta, 1);
        setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    };

    // TEMP: paid/Utbetald header disabled for now
    // const paidItems    = summaryItems.filter(i => i.status === "Utbetald" || i.status === "Godkänd");
    const pendingItems = summaryItems.filter(i => i.status === "Skickat");
    const deniedItems  = summaryItems.filter(i => i.status === "Nekad");
    const draftItems   = summaryItems.filter(i => i.status === "Förslag");
    const sum = (arr) => arr.reduce((acc, i) => acc + (i.grossAmount ?? 0), 0);

    const [year, monthNum] = selectedMonth.split("-").map(Number);
    const lastDay = new Date(year, monthNum, 0).getDate();
    const monthShort = new Date(year, monthNum - 1, 1)
        .toLocaleDateString("sv-SE", { month: "long" });

    const cards = [
        // TEMP: paid/Utbetald card disabled for now
        // {
        //     key: "paid",
        //     title: "Utbetalda ärenden",
        //     amount: sum(paidItems),
        //     subtitle: `Period: 1 – ${lastDay} ${monthShort}`,
        //     bg: "#E8F5EE", accent: "#157553",
        //     icon: <i className="bx bx-party rh-theme-icon rh-theme-icon-paid" />,
        //     items: paidItems,
        // },
        {
            key: "pending",
            title: "Inväntar godkännande",
            amount: sum(pendingItems),
            subtitle: `Inskickade ärenden: ${pendingItems.length} st`,
            bg: "#E8EEF8", accent: "#2B5BB8",
            icon: <i className="bx bxs-hourglass-top rh-theme-icon rh-theme-icon-pending" />,
            items: pendingItems,
        },
        {
            key: "denied",
            title: "Nekade ärenden",
            amount: sum(deniedItems),
            subtitle: `Nekade ärenden: ${deniedItems.length} st`,
            bg: "#FCEAEA", accent: "#C0392B",
            icon: <i className="bx bx-x-circle rh-theme-icon rh-theme-icon-denied" />,
            items: deniedItems,
        },
        {
            key: "draft",
            title: "Förslag",
            amount: `${draftItems.length} pågående`,
            //subtitle: `${draftItems.length} pågående`,
            bg: "#FFF4CC", accent: "#8A6500",
            icon: <i className="bx bxs-pencil rh-theme-icon rh-theme-icon-draft" />,
            items: draftItems,
        },
    ];

    // ── The button is defined here and passed down as JSX ──
    const createButton = permission && !isReadOnly ? (
        <CustomButton
            buttonWidth="block"
            customClasses="justify-content-center reimbursement-create-btn"
            text={t('reimbursement.newProcessing')}
            icon="bx bxs-plus-circle pe-2"
            buttonColor="#084F35"
            hoverButtonColor="#157553"
            onClick={() => {
                if (userHasBankAccount === false) {
                    FlashMessage(
                        <div className="p-0 position-relative">
                            <i id="closeButtonMessage" className="bx bx-x fs-4 position-absolute end-0 z-3 reimbursement-close-message-icon" />
                            <p className="mb-0 pt-1">
                                {t('validationMessage.needAccount')}{' '}
                                <a className='underline' href={USER_PATHS.SETTINGS}>
                                    {t('validationMessage.goAddAccount')}
                                </a>
                            </p>
                        </div>,
                        'flash-messageError',
                        null,
                        true
                    );
                } else {
                    setSelectOrgOpen(true);
                }
            }}
            buttonSize="big"
        />
    ) : null;

    const columns = ["visualId", 'date', 'purpose', 'orgName', 'grossAmount', 'createdAt', 'status'];
    const listOptions = {
        "model": ResponseReimbursement,
        "columns": [
            "visualId",
            'date',
            {
                "field" : "purpose",
                "label" : t('reimbursement.purpose'),
                "tdClass": (item) => "auto-wrap-td",
                "value": (item) => item.purpose
            },
            {
                "field": "reimbursementTypes",
                "label": t('modelLabel.type'),
                "sortable": false,
                "value": (item) => <ReimbursementTypeBadges types={getReimbursementTypesFromReimbursement(item)} center />
            },
            {
                "field": "orgName",
                "label": t('modelLabel.org'),
                "tdClass": (item) => "auto-wrap-td",
                "value": (item) => item.orgName
            },
            {
                "field" : "grossAmount",
                "label" : t('modelLabel.grossAmount'),
                'insideClass' : (item)=> item.grossAmount >= 0 ? 'text-green wrap' : 'text-red wrap',
                "value": (item) => item.grossAmount ? ToCustomDecimal(item.grossAmount) +' kr' : "",
                'type': 'number' ,
            },
            'createdAt',
            'status',
        ],
        "actions": {
            "actionColumn" : renderActionColumn,
            //"addButton": renderButtonColumn,
        },
        "exportIsAvailable": true,
        "filterTestIsAvailable": true,
        "urlGetListItemsToExport": `${baseUrl}${query}`,
        "nameFile": t('reimbursement.reportProcessings'),
        "modelType" : "Reimbursement",
        "ignoreFields" : columns,
        "urlGetSearch" : `${baseUrl}${query}`,
        "viewName": "ReportProceedingList",
        "ignoreUrlParams": !!userId,
        "groupByMonth": true,
    };

    return (
        <>
            <CustomModal
                isOpen={displayDeleteModal}
                toggle={() => setDisplayDeleteModal(!displayDeleteModal)}
                body={modalContent}
                footer={modalFooter}
            />

            <SlidePanel
                isOpen={panelOpen}
                onClose={() => setPanelOpen(false)}
                title={t('reimbursement.reportProcessing') + (infoPanelVisualId ? ' ' + infoPanelVisualId : '')}
            >
                {panelItemId && <ReportProceedingInfo id={panelItemId} onClose={() => setPanelOpen(false)} />}
            </SlidePanel>

            <SelectOrgPanel
                isOpen={selectOrgOpen}
                onClose={() => setSelectOrgOpen(false)}
            />

            <Col lg="12" className={`mb-5 ${isMobile ? "p-0" : "p-4 mx-0 px-0 pt-0"}`}>
                {isMobile ? (
                    mobileShowAll || !SHOW_SUMMARY_BOXES ? (
                        <>
                            {!SHOW_SUMMARY_BOXES && createButton && <div className="px-3 mb-3">{createButton}</div>}
                            <SortableList listOptions={listOptions} />
                        </>
                    ) : (
                        <ReimbursementHeaderMobile
                            title={t("reimbursement.reportProcessings")}
                            selectedMonth={selectedMonth}
                            onPrevMonth={() => changeMonth(-1)}
                            onNextMonth={() => changeMonth(1)}
                            cards={cards}
                            loading={summaryLoading}
                            createButton={createButton}
                            onShowAll={() => setMobileShowAll(true)}
                            renderItemActions={(item) => renderActionColumn(item)}
                        />
                    )
                ) : (
                    <>
                        <ReimbursementHeader
                            createButton={createButton}
                            selectedMonth={selectedMonth}
                            onPrevMonth={() => changeMonth(-1)}
                            onNextMonth={() => changeMonth(1)}
                            cards={cards}
                            loading={summaryLoading}
                            showSummary={SHOW_SUMMARY_BOXES}
                            renderItemActions={(item) => renderActionColumn(item)}
                        />
                        <SortableList listOptions={listOptions} />
                    </>
                )}
            </Col>
        </>
    );
};

export default ReportProceedingList;